import React, { useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, Box,
  TableHead, TableRow, Paper, Typography, IconButton,
  Stack, GlobalStyles, Tooltip, Popover, TextField,
  TablePagination, Button, Checkbox
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FilterListIcon from '@mui/icons-material/FilterList';
import DateRangeIcon from '@mui/icons-material/DateRange';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { GridLoader } from 'react-spinners';
import { parse, isValid, format } from 'date-fns';
import { useEffect } from 'react';
import { textAlign } from '@mui/system';

const ReusableTable = ({ columns, rows, onEdit, onDelete, pdf, onPdf, editicon, deleteicon, onLeadNameClick, popup, onApplyFilters, onPaginationChange, totalrows, onSelectionChange }) => {
  const [filters, setFilters] = useState({});
  const [dateRangeFilters, setDateRangeFilters] = useState({});
  const [filteravailable, setFilteravailable] = useState(false);
  const [numberRangeFilters, setNumberRangeFilters] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeField, setActiveField] = useState('');
  const [filterType, setFilterType] = useState('text');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [expand, setExpand] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);


  const handleOpenFilter = (event, field) => {
    setAnchorEl(event.currentTarget);
    setActiveField(field);
    const column = columns.find(col => col.field === field);
    if (column?.type === 'date' || isDateField(field)) {
      setFilterType('date');
    } else if (column?.type === 'number' || isNumberField(field)) {
      setFilterType('number');
    } else {
      setFilterType('text');
    }
  };

  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(selectedRows);
    }
  }, [selectedRows]);

  const handleCloseFilter = () => {
    setAnchorEl(null);
    setActiveField('');
    setFilterType('text');
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };
  useEffect(() => {
    if (onPaginationChange) {
      onPaginationChange({
        page: page,
        rowsPerPage: rowsPerPage,
      });
    }
  }, []);

  const handleDateRangeChange = (field, range, value) => {
    setDateRangeFilters(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        [range]: value
      }
    }));
  };

  const handleNumberRangeChange = (field, range, value) => {
    setNumberRangeFilters(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        [range]: value
      }
    }));
  };
  const applyFilter = () => {
    setFilteravailable(true);
    setPage(0);
    handleCloseFilter();

    if (onApplyFilters) {
      let applied = [];

      Object.entries(filters).forEach(([field, value]) => {
        if (value && value !== "") {
          applied.push({ field, value });
        }
      });

      // Date range filters
      Object.entries(dateRangeFilters).forEach(([field, range]) => {
        if (range?.start || range?.end) {
          applied.push({
            field,
            start: range?.start || null,
            end: range?.end || null
          });
        }
      });

      // Number range filters
      Object.entries(numberRangeFilters).forEach(([field, range]) => {
        if (range?.min || range?.max) {
          applied.push({
            field,
            min: range?.min || null,
            max: range?.max || null
          });
        }
      });

      onApplyFilters(applied);
    }
    setFilteravailable(false)
  };


  const clearFilter = (field) => {
    // Remove from local draft states
    if (dateRangeFilters[field]) {
      setDateRangeFilters(prev => {
        const newFilters = { ...prev };
        delete newFilters[field];
        return newFilters;
      });
    } else if (numberRangeFilters[field]) {
      setNumberRangeFilters(prev => {
        const newFilters = { ...prev };
        delete newFilters[field];
        return newFilters;
      });
    } else {
      setFilters(prev => {
        const newFilters = { ...prev };
        delete newFilters[field];
        return newFilters;
      });
    }

    let updated = [];

    Object.entries(filters).forEach(([f, value]) => {
      if (f !== field && value && value !== "") {
        updated.push({ field: f, value });
      }
    });

    Object.entries(dateRangeFilters).forEach(([f, range]) => {
      if (f !== field && (range?.start || range?.end)) {
        updated.push({ field: f, start: range?.start || null, end: range?.end || null });
      }
    });

    Object.entries(numberRangeFilters).forEach(([f, range]) => {
      if (f !== field && (range?.min || range?.max)) {
        updated.push({ field: f, min: range?.min || null, max: range?.max || null });
      }
    });

    if (onApplyFilters) {
      onApplyFilters(updated);
    }

    setFilteravailable(updated.length > 0);
    setPage(0);
    handleCloseFilter();
  };


  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    if (onPaginationChange) {
      onPaginationChange({ page: newPage, rowsPerPage });
    }
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    if (onPaginationChange) {
      onPaginationChange({ page: 0, rowsPerPage: newRowsPerPage });
    }
  };

  const formattedDate = (inputDate) => {
    if (!inputDate) return '';
    const date = parse(inputDate, 'yyyy-MM-dd', new Date());
    if (!isValid(date)) return inputDate;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  if (!columns.length) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 5 }}>
        <GridLoader color="#36d7b7" size={20} speedMultiplier={2} />
        <Typography variant="body2" sx={{ mt: 2 }}>Loading data...</Typography>
      </Box>
    );
  };


  const colors = (status) => {
    switch (status) {
      case 'Pending': return '#02046C';
      case 'Completed': return '#146705';
      case 'Rejected': return '#A01308';
      case 'Inactive': return '#A01308';
      case 'inactive': return '#A01308';
      case 'Active': return '#146705';
      case 'active': return '#146705';
      default: return 'inherit';
    }
  };

  const getServices = (row) => {
    try {
      return Array.isArray(row.services)
        ? row.services
        : JSON.parse(row.services || '[]');
    } catch (e) {
      return [];
    }
  };

  const isDateField = (field) => {
    const column = columns.find(col => col.field === field);
    return column?.type === 'date' ||
      field.toLowerCase().includes('date') ||
      field.toLowerCase().includes('time');
  };

  const isNumberField = (field) => {
    const column = columns.find(col => col.field === field);
    return column?.type === 'number' ||
      ['amount', 'price', 'quantity', 'total', 'count'].some(word =>
        field.toLowerCase().includes(word)
      );
  };

  const hasActiveFilter = (field) => {
    return (
      (filters[field] && filters[field] !== '') ||
      (dateRangeFilters[field] && (dateRangeFilters[field].start || dateRangeFilters[field].end)) ||
      (numberRangeFilters[field] && (numberRangeFilters[field].min || numberRangeFilters[field].max))
    );
  };

  const filteredRows = filteravailable ? rows.filter((row) => {
    const textFilterPass = Object.entries(filters).every(([field, value]) => {
      if (!value || field.startsWith('service_')) return true;
      const cellValue = row[field];
      if (cellValue === null || cellValue === undefined) return false;
      return cellValue.toString().toLowerCase().includes(value.toLowerCase());
    });

    const dateFilterPass = Object.entries(dateRangeFilters).every(([field, range]) => {
      if (field.startsWith('service_')) return true;
      const cellValue = row[field];
      if (!cellValue) return true;
      const cellDate = parse(cellValue, 'dd/MM/yyyy', new Date());
      if (!isValid(cellDate)) return false;
      cellDate.setHours(0, 0, 0, 0);
      let startDate = range.start ? new Date(range.start) : null;
      let endDate = range.end ? new Date(range.end) : null;
      if (startDate && !isValid(startDate)) startDate = null;
      if (endDate && !isValid(endDate)) endDate = null;
      if (startDate) startDate.setHours(0, 0, 0, 0);
      if (endDate) endDate.setHours(23, 59, 59, 999);
      if (startDate && endDate) {
        return cellDate >= startDate && cellDate <= endDate;
      } else if (startDate) {
        return cellDate >= startDate;
      } else if (endDate) {
        return cellDate <= endDate;
      }
      return true;
    });

    const numberFilterPass = Object.entries(numberRangeFilters).every(([field, range]) => {
      if (field.startsWith('service_')) return true;
      const cellValue = parseFloat(row[field]);
      if (isNaN(cellValue)) return true;
      if (range.min !== undefined && range.min !== '') {
        const minValue = parseFloat(range.min);
        if (cellValue < minValue) return false;
      }
      if (range.max !== undefined && range.max !== '') {
        const maxValue = parseFloat(range.max);
        if (cellValue > maxValue) return false;
      }
      return true;
    });

    const serviceFilterPass = () => {
      const serviceFilters = Object.entries(filters).filter(([key]) => key.startsWith('service_'));
      const serviceDateFilters = Object.entries(dateRangeFilters).filter(([key]) => key.startsWith('service_'));
      const serviceNumberFilters = Object.entries(numberRangeFilters).filter(([key]) => key.startsWith('service_'));

      if (serviceFilters.length === 0 && serviceDateFilters.length === 0 && serviceNumberFilters.length === 0) {
        return true;
      }

      // robust parsers
      const parseServiceCellDate = (val) => {
        if (!val) return null;
        let d = parse(val, 'dd/MM/yyyy', new Date());
        if (!isValid(d)) d = parse(val, 'yyyy-MM-dd', new Date());
        if (!isValid(d)) d = parse(val, 'dd/MM/yyyy HH:mm', new Date());
        if (!isValid(d)) d = parse(val, 'yyyy-MM-dd HH:mm', new Date());
        if (!isValid(d)) d = new Date(val); // ISO fallback
        return isValid(d) ? d : null;
      };

      const parsePickerDate = (val, endOfDay = false) => {
        if (!val) return null;
        // <input type="date" /> gives 'yyyy-MM-dd'
        let d = parse(val, 'yyyy-MM-dd', new Date());
        if (!isValid(d)) d = new Date(val);
        if (!isValid(d)) return null;
        d.setHours(endOfDay ? 23 : 0, endOfDay ? 59 : 0, endOfDay ? 59 : 0, endOfDay ? 999 : 0);
        return d;
      };

      const services = getServices(row);

      return services.some(service => {
        // text filters on services
        const textMatch = serviceFilters.every(([field, value]) => {
          if (!value) return true;
          const serviceField = field.replace('service_', '');
          const cellValue = service[serviceField];
          if (cellValue === null || cellValue === undefined) return false;
          return cellValue.toString().toLowerCase().includes(value.toLowerCase());
        });

        // date range filters on services (start_date, end_date, etc.)
        const dateMatch = serviceDateFilters.every(([field, range]) => {
          const serviceField = field.replace('service_', '');
          const raw = service[serviceField] ?? service[`display_${serviceField}`];
          if (!raw) return true;

          const cellDate = parseServiceCellDate(raw);
          if (!cellDate) return false;
          cellDate.setHours(0, 0, 0, 0);

          const startDate = parsePickerDate(range.start, false);
          const endDate = parsePickerDate(range.end, true);

          if (startDate && cellDate < startDate) return false;
          if (endDate && cellDate > endDate) return false;
          return true;
        });

        // number range filters on services
        const numberMatch = serviceNumberFilters.every(([field, range]) => {
          const serviceField = field.replace('service_', '');
          const cellValue = parseFloat(service[serviceField]);
          if (isNaN(cellValue)) return true;
          if (range.min !== undefined && range.min !== '' && cellValue < parseFloat(range.min)) return false;
          if (range.max !== undefined && range.max !== '' && cellValue > parseFloat(range.max)) return false;
          return true;
        });

        return textMatch && dateMatch && numberMatch;
      });
    };


    return textFilterPass && dateFilterPass && numberFilterPass && serviceFilterPass();
  }) : rows;


  const pagesPerChunk = Math.ceil(100 / rowsPerPage);

  const startIndex = (page % pagesPerChunk) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;

  const paginatedRows = filteredRows.slice(startIndex, endIndex);

  // const paginatedRows = filteredRows.slice(
  //   page * rowsPerPage,
  //   page * rowsPerPage + rowsPerPage
  // );
  // const paginatedRows = filteredRows

  const isDateInPast = (dateString) => {
    if (!dateString) return false;
    const date = parse(dateString, 'dd/MM/yyyy', new Date());
    if (!isValid(date)) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const showActionColumn = (editicon || deleteicon || pdf);

  return (
    <>
      <GlobalStyles styles={{
        '.custom-scrollbar::-webkit-scrollbar': {
          width: '8px',
          height: '4px',
        },
        '.custom-scrollbar::-webkit-scrollbar-track': {
          backgroundColor: '#f0f0f0',
        },
        '.custom-scrollbar::-webkit-scrollbar-thumb': {
          backgroundColor: '#0b85c8',
          borderRadius: '10px',
        },
        '.custom-scrollbar::-webkit-scrollbar-thumb:hover': {
          backgroundColor: '#0a6fa1',
        },
      }} />

      <TableContainer component={Paper} className="custom-scrollbar"
        sx={{
          mb: 1,
          position: 'relative',
          overflow: 'auto',
          maxHeight: 'calc(100vh - 180px)',
          '&::-webkit-scrollbar': {
            width: '6px',
            height: '6px',
          },
        }}>
        <Table sx={{ minWidth: 650 }} size='small' stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={{ bgcolor: '#f0f9ff' }}>
                <Checkbox
                  indeterminate={
                    selectedRows.length > 0 &&
                    selectedRows.length < paginatedRows.length
                  }
                  checked={
                    paginatedRows.length > 0 &&
                    selectedRows.length === paginatedRows.length
                  }
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedRows(paginatedRows.map((row) => row.id));
                    } else {
                      setSelectedRows([]);
                    }
                  }}
                />
              </TableCell>

              {columns.map((col) => {
                if (col.field !== 'services') {
                  return (
                    <TableCell
                      key={col.field}
                      sx={{
                        fontSize: 13,
                        fontFamily: 'Open Sans',
                        textWrap: 'nowrap',
                        fontWeight: 600,
                        bgcolor: '#f0f9ff',
                      }}
                    >
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        {col.headerName}
                        {(col.field !== 'id' && col.field !== 'notes' && col.field !== 'followUpTime' && col.field !== 'payingNow') && (
                          <Tooltip title={
                            col.type === 'date' ? "Date filter" :
                              col.type === 'number' ? "Number range filter" :
                                "Text filter"
                          }>
                            <IconButton
                              size="small"
                              onClick={(e) => { handleOpenFilter(e, col.field) }}
                              sx={{
                                p: 0,
                                ml: 1,
                                color: hasActiveFilter(col.field) ? '#0b85c8' : 'inherit'
                              }}
                            >
                              {hasActiveFilter(col.field) ? (
                                <FilterAltIcon fontSize="small" />
                              ) : col.type === 'date' ? (
                                <DateRangeIcon fontSize="small" />
                              ) : col.type === 'number' ? (
                                <FilterListIcon fontSize="small" />
                              ) : (
                                <FilterListIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  );
                }
                return [
                  <TableCell key="service_name" sx={{ fontSize: 13, fontFamily: 'Open Sans', fontWeight: 600, minWidth: 120, wordBreak: 'break-word', bgcolor: '#f0f9ff' }}>Service Name</TableCell>,
                  <TableCell key="duration" sx={{ fontSize: 13, fontFamily: 'Open Sans', fontWeight: 600, minWidth: 80, wordBreak: 'break-word', bgcolor: '#f0f9ff', textWrap: 'nowrap' }}>Duration</TableCell>,
                  <TableCell key="base_price" sx={{ fontSize: 13, fontFamily: 'Open Sans', fontWeight: 600, minWidth: 100, wordBreak: 'break-word', bgcolor: '#f0f9ff' }}>Base Price</TableCell>,
                  <TableCell key="tax_rate" sx={{ fontSize: 13, fontFamily: 'Open Sans', fontWeight: 600, minWidth: 80, wordBreak: 'break-word', bgcolor: '#f0f9ff' }}>Tax</TableCell>,
                  <TableCell key="start_date" sx={{ fontSize: 13, fontFamily: 'Open Sans', fontWeight: 600, minWidth: 110, wordBreak: 'break-word', bgcolor: '#f0f9ff' }}>Start Date</TableCell>,
                  <TableCell key="end_date" sx={{ fontSize: 13, fontFamily: 'Open Sans', fontWeight: 600, minWidth: 110, wordBreak: 'break-word', bgcolor: '#f0f9ff' }}>End Date</TableCell>,
                  <TableCell key="originalduration" sx={{ fontSize: 13, fontFamily: 'Open Sans', fontWeight: 600, minWidth: 80, wordBreak: 'break-word', bgcolor: '#f0f9ff' }}>Req Duration</TableCell>,
                  //   <Box display="flex" alignItems="center" justifyContent="space-between">
                  //     Status
                  //     <Tooltip title="Text filter">
                  //       <IconButton
                  //         size="small"
                  //         onClick={(e) => handleOpenFilter(e, 'service_status')}
                  //         sx={{
                  //           p: 0,
                  //           ml: 1,
                  //           color: hasActiveFilter('service_status') ? '#0b85c8' : 'inherit'
                  //         }}
                  //       >
                  //         {hasActiveFilter('service_status') ? (
                  //           <FilterAltIcon fontSize="small" />
                  //         ) : (
                  //           <FilterListIcon fontSize="small" />
                  //         )}
                  //       </IconButton>
                  //     </Tooltip>
                  //   </Box>
                  // </TableCell>,
                ];
              })}
              {showActionColumn && (
                <TableCell
                  sx={{
                    fontSize: 13,
                    fontFamily: 'Open Sans',
                    textWrap: 'nowrap',
                    fontWeight: 600,
                    bgcolor: '#f0f9ff',
                  }}
                >
                  Action
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          {rows.length > 0 ? (
            <TableBody>
              {paginatedRows.map((row, index) => {
                const services = getServices(row);

                return (
                  <TableRow key={row.id || index}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedRows.includes(row.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedRows((prev) => [...prev, row.id]);
                          } else {
                            setSelectedRows((prev) => prev.filter((id) => id !== row.id));
                          }
                        }}
                      />
                    </TableCell>
                    {columns.map((col) => {
                      if (col.field === 'id') {
                        return (

                          <TableCell key={col.field} sx={{ fontFamily: 'Open Sans', fontSize: '12px' }}>
                            {page * rowsPerPage + index + 1}
                          </TableCell>
                        );
                      }



                      if (col.field === 'status') {
                        return (
                          <TableCell
                            key={col.field}
                            sx={{
                              fontFamily: 'Open Sans',
                              fontSize: '12px',
                              textWrap: 'nowrap',
                              color: colors(row[col.field]),
                              fontWeight: 'bold'
                            }}
                          >
                            {row[col.field]}
                          </TableCell>
                        );
                      }

                      if (col.field === 'notes' || col.field === 'failure_reasons') {
                        return (
                          <TableCell
                            key={col.field}
                            onClick={() => {
                              setExpand(
                                expand === index ? null : index
                              );
                            }}
                            sx={{
                              fontFamily: 'Open Sans',
                              fontSize: '12px',
                              whiteSpace: expand === index ? 'normal' : 'nowrap',
                              textWrap: expand === index ? 'wrap' : 'nowrap',
                              width: '100px',
                              maxWidth: '100px',
                              minWidth: '100px',
                              overflow: 'hidden',
                              cursor: 'pointer',
                              textOverflow: expand === index ? 'clip' : 'ellipsis',
                            }}
                          >
                            {row[col.filed]?.length > 0 ? row[col.field].charAt(0).toUpperCase() + row[col.field].slice(1) : 'Not mentioned'}
                          </TableCell>
                        );
                      }
                      if (col.field === 'services') {
                        return (
                          <>
                            <TableCell key="service_name" sx={{ fontSize: 12, fontFamily: 'Open Sans', fontWeight: 500, minWidth: 120, wordBreak: 'break-word' }}>
                              {services.map((service, index) => <div key={index}>{service.service_name || '-'}</div>)}
                            </TableCell>
                            <TableCell key="originalduration" sx={{ fontSize: 12, fontFamily: 'Open Sans', fontWeight: 500, minWidth: 120, wordBreak: 'break-word' }}>
                              {services.map((service, index) => <div key={index}>{service.original_duration || '-'}</div>)}
                            </TableCell>
                            <TableCell key="base_price" sx={{ fontSize: 12, fontFamily: 'Open Sans', fontWeight: 500, minWidth: 120, wordBreak: 'break-word' }}>
                              {services.map((service, index) => <div key={index}>{service.base_price || '-'}</div>)}
                            </TableCell>
                            <TableCell key="tax_rate" sx={{ fontSize: 12, fontFamily: 'Open Sans', fontWeight: 500, minWidth: 120, wordBreak: 'break-word' }}>
                              {services.map((service, index) => <div key={index}>{service.tax_rate || 0}%</div>)}
                            </TableCell>
                            <TableCell key="start_date" sx={{ fontSize: 12, fontFamily: 'Open Sans', fontWeight: 500, minWidth: 120, wordBreak: 'break-word' }}>
                              {services.map((service, index) => (
                                <div key={index}>
                                  {service.display_start_date || (service.start_date ? formattedDate(service.start_date) : '-')}
                                </div>
                              ))}
                            </TableCell>
                            <TableCell key="end_date" sx={{ fontSize: 12, fontFamily: 'Open Sans', fontWeight: 500, minWidth: 120, wordBreak: 'break-word' }}>
                              {services.map((service, index) => (
                                <div key={index}>
                                  {service.display_end_date || (service.end_date ? formattedDate(service.end_date) : '-')}
                                </div>
                              ))}
                            </TableCell>
                            <TableCell key="duration" sx={{ fontSize: 12, fontFamily: 'Open Sans', fontWeight: 500, minWidth: 120, wordBreak: 'break-word' }}>
                              {services.map((service, index) => <div key={index}>{service.duration || '-'}</div>)}
                            </TableCell>
                            {/* <TableCell key="status" sx={{ fontSize: 12, fontFamily: 'Open Sans', fontWeight: 500, minWidth: 120, wordBreak: 'break-word' }}>
                                                  {services.map((service, index) => {
                                                    const isInactive = isDateInPast(service.display_end_date || service.end_date);
                                                    const status = isInactive ? 'Inactive' : (service.status || 'Active');
                                                    return (
                                                      <div key={index} style={{ color: colors(status), fontWeight: 600 }}>
                                                        {status}
                                                      </div>
                                                    );
                                                  })}
                                                </TableCell> */}
                          </>
                        );
                      }
                      if (!popup) {
                        if (col.field === 'lead_name') {
                          return (
                            <TableCell
                              key={col.field}
                              onClick={() => onLeadNameClick(row)}
                              sx={{
                                fontFamily: 'Open Sans',
                                fontSize: '12px',
                                textWrap: 'nowrap',
                                cursor: 'pointer',
                                color: '#0b85c8',
                                textDecoration: 'none'
                              }}
                            >
                              {row[col.field]}
                            </TableCell>
                          );
                        }
                      }

                      return (

                        <TableCell key={col.field} sx={{ fontFamily: 'Open Sans', fontSize: '12px', textWrap: 'nowrap', textAlign: (!row[col.field] || String(row[col.field]).trim().length === 0) ? 'center' : 'left' }}>
                          {typeof row[col.field] === 'string' && row[col.field].length > 0
                            ? row[col.field].charAt(0).toUpperCase() + row[col.field].slice(1)
                            : row[col.field] != null
                              ? String(row[col.field]).length > 0 ? String(row[col.field])
                                : '-' : '-'}
                        </TableCell>
                      );
                    })}
                    {showActionColumn && (
                      <TableCell sx={{ fontFamily: 'Open Sans', fontSize: '12px', textWrap: 'nowrap' }}>
                        <Stack direction="row" spacing={1} justifyContent="center">
                          {editicon && <Tooltip arrow title='Edit'>
                            <IconButton size="small" onClick={() => onEdit(row)}>
                              <EditIcon sx={{ color: "#0b85c8", fontSize: '18px' }} />
                            </IconButton>
                          </Tooltip>}
                          {deleteicon &&
                            <Tooltip title='Delete' arrow>
                              <IconButton size="small" onClick={() => onDelete(row)}>
                                <DeleteIcon sx={{ color: "red", fontSize: '18px' }} />
                              </IconButton>
                            </Tooltip>
                          }
                          {pdf && (
                            <Tooltip arrow title="Actions">
                              <IconButton size="small" onClick={() => onPdf(row)} sx={{ minWidth: 'auto', p: '2px' }}>
                                <MoreVertIcon sx={{ color: "#888", fontSize: '18px' }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>)
            : (
              <TableBody></TableBody>
            )}
        </Table>
      </TableContainer >
      {rows.length === 0 && <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden'
      }}>
        <img
          src='./no data found.png'
          alt="No data found"
          style={{ maxWidth: 250, marginBottom: 1, filter: 'drop-shadow(0px 0px 5px rgba(158, 153, 153, 0.5))' }}
        />
        <Typography variant="h6" sx={{ fontWeight: '600', fontFamily: 'Open Sans' }}>
          No Data Found
        </Typography>
      </Box>}

      <TablePagination
        rowsPerPageOptions={[25, 50, 100]}
        component="div"
        count={totalrows}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{
          '& .MuiTablePagination-toolbar': {
            paddingLeft: 2,
          }
        }}
      />

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleCloseFilter}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          '& .MuiPaper-root': {
            p: 2,
            minWidth: 300,
          }
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 14, mb: 1.5, fontWeight: 600 }}>
            Filter by {activeField}
          </Typography>

          {filterType === 'date' ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                type='date'
                label="Start Date"
                value={dateRangeFilters[activeField]?.start || ''}
                onChange={(e) => handleDateRangeChange(activeField, 'start', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                type='date'
                label="End Date"
                value={dateRangeFilters[activeField]?.end || ''}
                onChange={(e) => handleDateRangeChange(activeField, 'end', e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: dateRangeFilters[activeField]?.start || ''
                }}
              />
              <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
                <Button
                  size="small"
                  onClick={() => clearFilter(activeField)}
                  disabled={!dateRangeFilters[activeField]?.start && !dateRangeFilters[activeField]?.end}
                >
                  Clear
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={applyFilter}
                  disabled={!dateRangeFilters[activeField]?.start && !dateRangeFilters[activeField]?.end}
                >
                  Apply
                </Button>
              </Stack>
            </Box>
          ) : filterType === 'number' ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Min Value"
                type="number"
                size="small"
                value={numberRangeFilters[activeField]?.min || ''}
                onChange={(e) => handleNumberRangeChange(activeField, 'min', e.target.value)}
                fullWidth
                inputProps={{
                  max: numberRangeFilters[activeField]?.max || ''
                }}
              />
              <TextField
                label="Max Value"
                type="number"
                size="small"
                value={numberRangeFilters[activeField]?.max || ''}
                onChange={(e) => handleNumberRangeChange(activeField, 'max', e.target.value)}
                fullWidth
                inputProps={{
                  min: numberRangeFilters[activeField]?.min || ''
                }}
              />
              <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
                <Button
                  size="small"
                  onClick={() => clearFilter(activeField)}
                  disabled={!numberRangeFilters[activeField]?.min && !numberRangeFilters[activeField]?.max}
                >
                  Clear
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={applyFilter}
                  disabled={!numberRangeFilters[activeField]?.min && !numberRangeFilters[activeField]?.max}
                >
                  Apply
                </Button>
              </Stack>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                autoFocus
                size="small"
                placeholder={`Filter ${activeField}`}
                value={filters[activeField] || ''}
                onChange={(e) => handleFilterChange(activeField, e.target.value)}
                fullWidth
              />
              <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
                <Button
                  size="small"
                  onClick={() => clearFilter(activeField)}
                  disabled={!filters[activeField]}
                >
                  Clear
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={applyFilter}
                  disabled={!filters[activeField]}
                >
                  Apply
                </Button>
              </Stack>
            </Box>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default ReusableTable;